import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Film, Clapperboard, Upload, LogOut, Menu, X,
} from "lucide-react";

const BLUE_CROSS_LOGO = "/cross-logo.png";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/mrs", label: "MR Management", icon: Users },
  { to: "/admin/template", label: "Template Video", icon: Film },
  { to: "/admin/videos", label: "Generated Videos", icon: Clapperboard },
];

const MR_NAV = [
  { to: "/mr", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/mr/upload", label: "Upload Video", icon: Upload },
  { to: "/mr/videos", label: "My Videos", icon: Clapperboard },
];

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? ADMIN_NAV : MR_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebarContent = (
    <>
      <div className="flex items-center px-6 py-6 border-b border-stone-200">
        <img
          src={BLUE_CROSS_LOGO}
          alt="Blue Cross Life Sciences"
          className="h-10 w-auto max-w-full"
        />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-50 text-emerald-800"
                  : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-stone-200 p-4">
        <div className="mb-3 px-2">
          <p className="text-sm font-semibold text-stone-800 truncate">{user?.name}</p>
          <p className="text-xs text-stone-500 capitalize">{user?.role === "admin" ? "Administrator" : "Medical Rep"}</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between border-b border-stone-200 bg-white px-4 py-3 sticky top-0 z-30">
        <img
          src={BLUE_CROSS_LOGO}
          alt="Blue Cross Life Sciences"
          className="h-8 w-auto"
        />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="p-2">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-stone-200">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 flex flex-col bg-white">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 p-1" data-testid="mobile-menu-close">
              <X className="h-5 w-5" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 animate-fade-up">
          {children}
        </div>
      </main>
    </div>
  );
};
